import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { PaleState } from '@/interfaces';
import { filtersToBrowserQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { stringifyParams } from '@/lib/utils/queryString';

export const SmallGeoMap = () => {
    const filters = useSelector((s: PaleState) => s.filters);
    const boundingBoxes = filters.boundingBox;
    const destinationLabel = filters.destinationLabel;

    const mapCenter = useMemo(() => {
        if (boundingBoxes?.length) {
            const minLat = Math.min(...boundingBoxes.map((b) => b.minLat));
            const minLon = Math.min(...boundingBoxes.map((b) => b.minLon));
            const maxLat = Math.max(...boundingBoxes.map((b) => b.maxLat));
            const maxLon = Math.max(...boundingBoxes.map((b) => b.maxLon));
            return { lat: (minLat + maxLat) / 2, lng: (minLon + maxLon) / 2 };
        }
        return { lat: 40, lng: 15 };
    }, [boundingBoxes]);

    const zoomLevel = useMemo(() => {
        if (!boundingBoxes?.length) return 4;
        const minLat = Math.min(...boundingBoxes.map((b) => b.minLat));
        const minLon = Math.min(...boundingBoxes.map((b) => b.minLon));
        const maxLat = Math.max(...boundingBoxes.map((b) => b.maxLat));
        const maxLon = Math.max(...boundingBoxes.map((b) => b.maxLon));
        const latDiff = Math.abs(maxLat - minLat);
        const lngDiff = Math.abs(maxLon - minLon);
        const maxDiff = Math.max(latDiff, lngDiff);

        if (maxDiff > 20) return 3;
        if (maxDiff > 10) return 4;
        if (maxDiff > 5) return 5;
        if (maxDiff > 2) return 6;
        if (maxDiff > 1) return 7;
        return 8;
    }, [boundingBoxes]);

    const staticMapImageUrl = `https://maptiles.finncdn.no/staticmap?lat=${mapCenter.lat}&lng=${mapCenter.lng}&zoom=${zoomLevel}&maptype=norwayVector&showPin=false&size=590x194`;

    const mapUrlWithFilters = useMemo(() => {
        const queryString = stringifyParams(filtersToBrowserQueryParams(filters));
        return queryString ? `/reise/pakkereiser/kart?${queryString}` : '/reise/pakkereiser/kart';
    }, [filters]);

    return (
        <a href={mapUrlWithFilters} className="block border rounded-8 mb-16" data-testid="smallGeoMap" aria-label="Vis i kart">
            <img
                src={staticMapImageUrl}
                alt={destinationLabel ? `Kart over ${destinationLabel}` : 'Vis på kart'}
                className="w-full rounded-t-8 cursor-pointer hover:opacity-90 transition-opacity"
            />
            <p className="text-center py-12 m-0">Vis i kart</p>
        </a>
    );
};
